import { type Material, type Mesh, ShaderMaterial } from 'three'
import { RoadWindow, ToonMaterial } from './toonMaterial'

/**
 * The shadow pass's copy of the road-window cut.
 *
 * `ToonMaterial` with `ROAD_CUT` discards every fragment whose arc distance
 * lies outside `RoadWindow`, so a bus driving into a tunnel stub loses its
 * body at the end of the tarmac. The sun's shadow map is drawn by a different
 * material — three's shared `MeshDepthMaterial` — which knows nothing of
 * `aRoadDistance`, so the part of the body the colour pass had discarded was
 * still written into the map and its shadow went on lying on the ground past
 * the cover while the bus itself was gone: a bus "cut away" with its shadow
 * intact.
 *
 * What the original does here is not readable. 🔍 The gameplay camera culls
 * every layer (`Gameplay.unity`:1877-1879, `m_CullingMask.m_Bits:
 * 4294967295`), the `Invisible` layer a stub bus is moved to included, and
 * 📐 URP's shadow-caster pass draws from that cull, so the stub bus does reach
 * the original's shadow map. Whether that shadow ever *shows* is decided by
 * the stencil render feature that hides the body — stripped to a name
 * (`URP_Renderer.asset`) — and by where the cover's footprint ends. ❓ Declared
 * in `GAMEPLAY-CHECKLIST.md` §10 beside the cut itself, which is a port-side
 * device to begin with (see `RoadWindow`). The port's rule is the consistent
 * one: **what the colour pass does not draw casts no shadow**, so the depth
 * pass runs the very same test on the very same attribute.
 *
 * This is `MeshDepthMaterial` with `RGBADepthPacking` — what
 * `WebGLShadowMap` uses for a directional light — rewritten as a
 * `ShaderMaterial` so the vertex stage can read `aRoadDistance` and the
 * fragment stage can discard on it. The depth formula and the packing are
 * copied from three's `depth.glsl.js`, reversed-depth branch included, because
 * the map is read back by three's own `unpackRGBAToDepth`; the packing on both
 * sides has to agree, and `roadCutShadow.test.ts` pins it to three's source.
 *
 * One instance for the whole level, like `RoadWindow` itself: the uniform is
 * shared by identity, so `SessionView.buildRoadWindow`'s single write reaches
 * the shadow pass as well as the colour pass, and no per-bus state lives here.
 * Instancing is applied by hand for the same reason `ToonMaterial` does it — a
 * `ShaderMaterial` writing its own `main()` gets the `USE_INSTANCING` define
 * from three but not the multiply.
 */
const VERTEX = /* glsl */ `
#include <common>
attribute float aRoadDistance;
varying float vRoadDistance;
varying vec2 vHighPrecisionZW;

void main() {
  mat4 instance = mat4(1.0);
  #ifdef USE_INSTANCING
    instance = instanceMatrix;
  #endif
  vec4 mvPosition = modelViewMatrix * instance * vec4(position, 1.0);
  gl_Position = projectionMatrix * mvPosition;
  vRoadDistance = aRoadDistance;
  vHighPrecisionZW = gl_Position.zw;
}
`

const FRAGMENT = /* glsl */ `
#include <common>
#include <packing>
uniform vec2 uRoadWindow;
varying float vRoadDistance;
varying vec2 vHighPrecisionZW;

void main() {
  // The same test ToonMaterial runs before it shades: a fragment past either
  // end of the drawn tarmac is not there, so it throws no shadow either.
  if (vRoadDistance < uRoadWindow.x || vRoadDistance > uRoadWindow.y) discard;

  // three's depth.glsl.js, verbatim: the high-precision equivalent of
  // gl_FragCoord.z, packed the way the shadow sampler unpacks it.
  #ifdef USE_REVERSED_DEPTH_BUFFER
    float fragCoordZ = vHighPrecisionZW[ 0 ] / vHighPrecisionZW[ 1 ];
  #else
    float fragCoordZ = 0.5 * vHighPrecisionZW[ 0 ] / vHighPrecisionZW[ 1 ] + 0.5;
  #endif
  gl_FragColor = packDepthToRGBA( fragCoordZ );
}
`

/** The one depth material every road-cut mesh casts through. */
export const ROAD_CUT_DEPTH_MATERIAL = new ShaderMaterial({
  name: 'RoadCutDepth',
  vertexShader: VERTEX,
  fragmentShader: FRAGMENT,
  uniforms: { uRoadWindow: RoadWindow },
})

/** Does this material discard against the road window? */
export function materialCutsRoad(material: Material): boolean {
  return material instanceof ToonMaterial && material.clip !== 0
}

/**
 * Give the mesh the cutting depth material exactly when its colour material
 * cuts, and take it away when the plain material comes back.
 *
 * Decided from the mesh's **current** material list, not from the mask the
 * caller holds, so the two passes cannot disagree: the seam twin, the reveal
 * twin and the hidden twin all carry `clip` on the material they were made
 * from, and this reads it off whichever one is mounted. Call after every
 * material swap — `SessionView.setBusClipped` — and copy the result onto a
 * mesh cloned from this one (`copyShadowCasting`).
 */
export function syncRoadCutShadow(mesh: Mesh): void {
  const material = mesh.material
  const cuts = Array.isArray(material) ? material.some(materialCutsRoad) : materialCutsRoad(material)
  mesh.customDepthMaterial = cuts ? ROAD_CUT_DEPTH_MATERIAL : undefined
}
